import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { sendErrorMessage } from '../../../../context/AssistantContext';

const TITLE_MAX = 120;

// Rename and delete for one assistant thread. Rendered once by
// AssistantProvider so the sidebar menu and the chat title bar share them.
function AssistantConversationDialogs({ renameTarget, deleteTarget, onRename, onDelete, onClose }) {
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setTitle(renameTarget?.title || '');
        setError('');
    }, [renameTarget]);
    useEffect(() => setError(''), [deleteTarget]);

    const trimmed = title.trim();
    const renameValid = trimmed.length > 0 && trimmed.length <= TITLE_MAX;
    const renameChanged = trimmed !== (renameTarget?.title || '').trim();

    const run = async (fn) => {
        setBusy(true);
        setError('');
        try {
            await fn();
            onClose();
        } catch (err) {
            setError(sendErrorMessage(err, 'انجام نشد؛ دوباره تلاش کنید.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Dialog
                open={Boolean(renameTarget)}
                onClose={() => !busy && onClose()}
                fullWidth
                maxWidth="xs"
                aria-labelledby="assistant-rename-title"
            >
                <DialogTitle id="assistant-rename-title">تغییر نام گفتگو</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        margin="dense"
                        label="عنوان"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.nativeEvent.isComposing && renameValid && renameChanged && !busy) {
                                event.preventDefault();
                                run(() => onRename(renameTarget.id, trimmed));
                            }
                        }}
                        error={Boolean(error) || (title.length > 0 && !renameValid)}
                        helperText={
                            error ||
                            (trimmed.length === 0
                                ? 'عنوان نمی‌تواند خالی باشد.'
                                : toPersianDigits(`${trimmed.length} از ${TITLE_MAX} نویسه`))
                        }
                        inputProps={{ maxLength: TITLE_MAX + 20 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={busy}>
                        انصراف
                    </Button>
                    <Button
                        variant="contained"
                        disabled={busy || !renameValid || !renameChanged}
                        onClick={() => run(() => onRename(renameTarget.id, trimmed))}
                    >
                        {busy ? <CircularProgress size={20} color="inherit" /> : 'ذخیره'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !busy && onClose()}
                aria-labelledby="assistant-delete-title"
            >
                <DialogTitle id="assistant-delete-title">حذف گفتگو</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`گفتگوی «${deleteTarget?.title || 'بدون عنوان'}» و همهٔ پیام‌های آن حذف می‌شود. ادامه می‌دهید؟`}
                    </DialogContentText>
                    {error && (
                        <DialogContentText color="error" sx={{ mt: 1 }}>
                            {error}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={busy}>
                        انصراف
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={busy}
                        onClick={() => run(() => onDelete(deleteTarget.id))}
                    >
                        {busy ? <CircularProgress size={20} color="inherit" /> : 'حذف'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default AssistantConversationDialogs;
